const PREFILL_MAX_ATTEMPTS = 24;
const PREFILL_INTERVAL_MS = 350;

const decodeHtml = (value) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const getSearchParams = () => new URLSearchParams(window.location.search);

const getPropertyParamsFromQuery = (params) => {
  const entries = [];
  for (const [key, value] of params.entries()) {
    if (key.startsWith('properties[')) {
      entries.push([key, decodeHtml(value)]);
    }
  }
  return entries;
};

const getPropertyValue = (propertyParams, propertyName) => {
  const key = `properties[${propertyName}]`;
  const match = propertyParams.find(([paramKey]) => paramKey === key);
  return match ? String(match[1] || '') : '';
};

const normalizeCode = (value) => String(value || '').trim().toUpperCase();

const getEditLineReference = (params) => {
  const lineItemKey = (params.get('line_item_key') || '').trim();
  const lineItemRaw = (params.get('line_item') || '').trim();
  const parsedLine = Number.parseInt(lineItemRaw, 10);
  const lineItem = Number.isFinite(parsedLine) && parsedLine > 0 ? parsedLine : null;
  return { lineItemKey, lineItem };
};

const toPropertyParamsFromCartItem = (item) => {
  if (!item || typeof item !== 'object' || !item.properties || typeof item.properties !== 'object') return [];

  const entries = [];
  for (const [propertyName, propertyValue] of Object.entries(item.properties)) {
    if (propertyValue == null) continue;
    const value = String(propertyValue);
    if (!value.trim()) continue;
    entries.push([`properties[${propertyName}]`, value]);
  }

  return entries;
};

const getPropertyParamsFromCart = async (params) => {
  const { lineItemKey, lineItem } = getEditLineReference(params);
  if (!lineItemKey && !lineItem) return [];

  try {
    const response = await fetch('/cart.js', { cache: 'no-store' });
    if (!response.ok) return [];
    const cart = await response.json();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return [];

    let matchedItem = null;
    if (lineItemKey) {
      matchedItem = items.find((item) => String(item?.key || '') === lineItemKey) || null;
    }

    if (!matchedItem && lineItem) {
      const itemByLine = items[lineItem - 1];
      if (itemByLine) matchedItem = itemByLine;
    }

    return toPropertyParamsFromCartItem(matchedItem);
  } catch (_error) {
    return [];
  }
};

const cssEscape =
  window.CSS && typeof window.CSS.escape === 'function'
    ? window.CSS.escape
    : (value) => value.replace(/([^\w-])/g, '\\$1');

const filledFields = new WeakSet();

const setFieldValue = (field, value) => {
  if (!(field instanceof HTMLElement)) return false;
  if (filledFields.has(field)) return false;

  const tagName = field.tagName.toLowerCase();
  const type = field instanceof HTMLInputElement ? field.type : '';

  if (type === 'file') return false;

  if (tagName === 'select') {
    const options = Array.from(field.options);
    let match = options.find((option) => option.value === value);
    if (!match) {
      match = options.find((option) => option.text.trim() === value);
    }
    if (!match) return false;
    field.value = match.value;
  } else if (type === 'radio') {
    if (field.value !== value) return false;
    field.checked = true;
  } else if (type === 'checkbox') {
    const normalized = value.trim().toLowerCase();
    const truthy = ['true', '1', 'yes', 'on'];
    field.checked = field.value === value || truthy.includes(normalized);
  } else if ('value' in field) {
    field.value = value;
  } else {
    return false;
  }

  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
  filledFields.add(field);
  return true;
};

const applyPrefill = (propertyParams) => {
  let matches = 0;
  for (const [key, value] of propertyParams) {
    const selector = `[name="${cssEscape(key)}"]`;
    const fields = document.querySelectorAll(selector);
    if (!fields.length) continue;
    fields.forEach((field) => {
      if (setFieldValue(field, value)) {
        matches += 1;
      }
    });
  }
  return matches;
};

const hasPendingFields = (propertyParams) => {
  for (const [key] of propertyParams) {
    const selector = `[name="${cssEscape(key)}"]`;
    const fields = document.querySelectorAll(selector);
    if (!fields.length) return true;
    let hasUnfilledField = false;
    fields.forEach((field) => {
      if (field instanceof HTMLElement && !filledFields.has(field)) {
        hasUnfilledField = true;
      }
    });
    if (hasUnfilledField) return true;
  }
  return false;
};

const createEmptyFigureState = () => ({
  face: '',
  hair: '',
  top: '',
  bottom: '',
  pets: {},
  addons: {},
});

const ensureFigureState = (state, figureNumber) => {
  if (!state[figureNumber]) {
    state[figureNumber] = createEmptyFigureState();
  }
  return state[figureNumber];
};

const parseBuilderThumbsState = (rawValue) => {
  const state = {};
  const raw = String(rawValue || '').trim();
  if (!raw) return state;

  raw.split('|').forEach((entry) => {
    const chunk = String(entry || '').trim();
    if (!chunk) return;

    const match = chunk.match(/^Fig\s*(\d+)\s*~\s*([^~]+?)\s*~\s*(.+)$/i);
    if (!match) return;

    const figureNumber = Number.parseInt(match[1], 10);
    if (!Number.isFinite(figureNumber) || figureNumber < 1) return;

    const label = String(match[2] || '').trim().toLowerCase();
    const code = normalizeCode(match[3]);
    if (!code) return;

    const figure = ensureFigureState(state, figureNumber);

    if (label === 'face') {
      figure.face = code;
      return;
    }
    if (label === 'hair') {
      figure.hair = code;
      return;
    }
    if (label === 'torso' || label === 'top') {
      figure.top = code;
      return;
    }
    if (label === 'legs' || label === 'bottom') {
      figure.bottom = code;
      return;
    }

    const petMatch = label.match(/^pet\s*(\d+)/i);
    if (petMatch) {
      const slot = Number.parseInt(petMatch[1], 10);
      if (Number.isFinite(slot) && slot > 0) figure.pets[slot] = code;
      return;
    }

    const addonMatch = label.match(/^add-?on\s*(\d+)/i);
    if (addonMatch) {
      const slot = Number.parseInt(addonMatch[1], 10);
      if (Number.isFinite(slot) && slot > 0) figure.addons[slot] = code;
    }
  });

  return state;
};

const parsePersonalizationState = (rawValue) => {
  const state = {};
  const raw = String(rawValue || '').trim();
  if (!raw) return state;

  raw.split(/\r?\n+/).forEach((line) => {
    const trimmedLine = String(line || '').trim();
    if (!trimmedLine) return;

    const lineMatch = trimmedLine.match(/^Figure\s+(\d+)\s*:\s*(.+)$/i);
    if (!lineMatch) return;

    const figureNumber = Number.parseInt(lineMatch[1], 10);
    if (!Number.isFinite(figureNumber) || figureNumber < 1) return;

    const details = lineMatch[2];
    const figure = ensureFigureState(state, figureNumber);

    const faceMatch = details.match(/Face:\s*([^,]+)/i);
    if (faceMatch) figure.face = normalizeCode(faceMatch[1]);

    const hairMatch = details.match(/Hair:\s*([^,]+)/i);
    if (hairMatch) figure.hair = normalizeCode(hairMatch[1]);

    const topMatch = details.match(/Top:\s*([^,]+)/i);
    if (topMatch) figure.top = normalizeCode(topMatch[1]);

    const bottomMatch = details.match(/Bottom:\s*([^,]+)/i);
    if (bottomMatch) figure.bottom = normalizeCode(bottomMatch[1]);
  });

  return state;
};

const getBuilderState = (propertyParams) => {
  const thumbsState = parseBuilderThumbsState(getPropertyValue(propertyParams, '_builder_thumbs'));
  if (Object.keys(thumbsState).length > 0) return thumbsState;
  const hiddenCodesState = parsePersonalizationState(getPropertyValue(propertyParams, '_builder_codes'));
  if (Object.keys(hiddenCodesState).length > 0) return hiddenCodesState;
  return parsePersonalizationState(getPropertyValue(propertyParams, 'Personalization'));
};

const getBuilderFigureNumbers = (builderState) =>
  Object.keys(builderState)
    .map((key) => Number.parseInt(key, 10))
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);

const getFigureBlock = (figureNumber) =>
  document.querySelector(`#figureBlocks .figure-block[data-figure="${figureNumber}"]`);

const ensureFigureBlockCount = (requiredCount) => {
  const container = document.getElementById('figureBlocks');
  if (!container) return false;
  if (requiredCount <= 1) return container.querySelectorAll('.figure-block').length >= 1;

  const addButton = document.getElementById('addFigureBtn');
  if (!(addButton instanceof HTMLElement)) {
    return container.querySelectorAll('.figure-block').length >= requiredCount;
  }

  let safety = 0;
  while (container.querySelectorAll('.figure-block').length < requiredCount && safety < 12) {
    addButton.click();
    safety += 1;
  }

  return container.querySelectorAll('.figure-block').length >= requiredCount;
};

const setBuilderFieldValue = (block, selector, value) => {
  const code = normalizeCode(value);
  if (!code) return false;

  const field = block ? block.querySelector(selector) : null;
  if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) {
    return false;
  }

  if (normalizeCode(field.value) === code) return false;

  field.value = code;
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
};

const isBuilderFieldPending = (block, selector, value) => {
  const expected = normalizeCode(value);
  if (!expected) return false;
  const field = block ? block.querySelector(selector) : null;
  if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) {
    return true;
  }
  return normalizeCode(field.value) !== expected;
};

const hasPendingBuilderPrefill = (builderState) => {
  const figureNumbers = getBuilderFigureNumbers(builderState);
  if (!figureNumbers.length) return false;

  for (const figureNumber of figureNumbers) {
    const figure = builderState[figureNumber];
    const block = getFigureBlock(figureNumber);
    if (!block) return true;

    if (isBuilderFieldPending(block, '.fig-face', figure.face)) return true;
    if (isBuilderFieldPending(block, '.fig-hair', figure.hair)) return true;
    if (isBuilderFieldPending(block, '.fig-top', figure.top)) return true;
    if (isBuilderFieldPending(block, '.fig-bottom', figure.bottom)) return true;

    for (const [slotRaw, code] of Object.entries(figure.pets || {})) {
      const slot = Number.parseInt(slotRaw, 10);
      if (Number.isFinite(slot) && isBuilderFieldPending(block, `.fig-pet[data-slot="${slot}"]`, code)) return true;
    }

    for (const [slotRaw, code] of Object.entries(figure.addons || {})) {
      const slot = Number.parseInt(slotRaw, 10);
      if (Number.isFinite(slot) && isBuilderFieldPending(block, `.fig-addon[data-slot="${slot}"]`, code)) return true;
    }
  }

  return false;
};

const applyBuilderPrefill = (builderState) => {
  const figureNumbers = getBuilderFigureNumbers(builderState);
  if (!figureNumbers.length) return true;

  const maxFigureNumber = figureNumbers[figureNumbers.length - 1];
  if (!ensureFigureBlockCount(maxFigureNumber)) return false;

  let changed = 0;
  figureNumbers.forEach((figureNumber) => {
    const figure = builderState[figureNumber];
    const block = getFigureBlock(figureNumber);
    if (!block) return;

    if (setBuilderFieldValue(block, '.fig-face', figure.face)) changed += 1;
    if (setBuilderFieldValue(block, '.fig-hair', figure.hair)) changed += 1;
    if (setBuilderFieldValue(block, '.fig-top', figure.top)) changed += 1;
    if (setBuilderFieldValue(block, '.fig-bottom', figure.bottom)) changed += 1;

    Object.entries(figure.pets || {}).forEach(([slotRaw, code]) => {
      const slot = Number.parseInt(slotRaw, 10);
      if (!Number.isFinite(slot)) return;
      if (setBuilderFieldValue(block, `.fig-pet[data-slot="${slot}"]`, code)) changed += 1;
    });

    Object.entries(figure.addons || {}).forEach(([slotRaw, code]) => {
      const slot = Number.parseInt(slotRaw, 10);
      if (!Number.isFinite(slot)) return;
      if (setBuilderFieldValue(block, `.fig-addon[data-slot="${slot}"]`, code)) changed += 1;
    });
  });

  if (changed > 0) {
    if (typeof window.scheduleSyncForms === 'function') {
      window.scheduleSyncForms();
    } else if (typeof window.updateOutput === 'function') {
      window.updateOutput();
    }
  }

  return !hasPendingBuilderPrefill(builderState);
};

const startPrefill = async () => {
  const params = getSearchParams();
  let propertyParams = getPropertyParamsFromQuery(params);
  if (!propertyParams.length) {
    propertyParams = await getPropertyParamsFromCart(params);
  }
  if (!propertyParams.length) return;

  const builderState = getBuilderState(propertyParams);

  let attempts = 0;
  let intervalId = null;
  const run = () => {
    attempts += 1;
    applyPrefill(propertyParams);
    const builderReady = applyBuilderPrefill(builderState);
    const complete = !hasPendingFields(propertyParams) && builderReady;
    if (complete || attempts >= PREFILL_MAX_ATTEMPTS) {
      if (intervalId) clearInterval(intervalId);
    }
  };

  run();
  intervalId = setInterval(run, PREFILL_INTERVAL_MS);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    startPrefill();
  });
} else {
  startPrefill();
}
